// Caster-level-scaled definitions for the "Add Common Buffs" picker.
// Each definition builds an ordinary personal buff (same shape as
// buffUtilities.createBuff) frozen at the chosen caster level; the caster
// level itself is never saved.
//
// A definition is { name, duration(cl) -> { value, unit }, effects(cl) -> [effect] }.

function effect(amount, category, target, bonusType) {
  return { amount: String(amount), category, target, bonusType };
}

function saves(amount, bonusType) {
  return ['fortitude', 'reflex', 'will'].map(save => effect(amount, 'defense', save, bonusType));
}

// Several spells only matter for tracking (no numeric bonus); they carry a
// +0 untyped Stealth effect so they still render as a valid buff.
function stealthTracker() {
  return [effect(0, 'skills', 'Stealth', 'untyped')];
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const rounds = value => ({ value, unit: 'rounds' });
const minutes = value => ({ value, unit: 'minutes' });
const hours = value => ({ value, unit: 'hours' });

const COMMON_BUFFS = [
  {
    name: 'Mage Armor',
    duration: cl => hours(cl),
    effects: () => [effect(4, 'defense', 'ac', 'armor')],
  },
  {
    name: 'Ironskin',
    // +4, +1 per 4 CL above 4th, max +7
    duration: cl => minutes(cl),
    effects: cl => [effect(clamp(4 + Math.floor(Math.max(0, cl - 4) / 4), 4, 7), 'defense', 'ac', 'enhancedNaturalArmor')],
  },
  {
    name: 'Shield',
    duration: cl => minutes(cl),
    effects: () => [effect(4, 'defense', 'ac', 'shield')],
  },
  {
    name: 'Shield of Faith',
    // +2, +1 per 6 CL, max +5
    duration: cl => minutes(cl),
    effects: cl => [effect(clamp(2 + Math.floor(cl / 6), 2, 5), 'defense', 'ac', 'deflection')],
  },
  {
    name: 'Barkskin',
    // +2, +1 per 3 CL above 3rd, max +5
    duration: cl => minutes(cl * 10),
    effects: cl => [effect(clamp(2 + Math.floor(Math.max(0, cl - 3) / 3), 2, 5), 'defense', 'ac', 'enhancedNaturalArmor')],
  },
  {
    name: 'Divine Favor',
    // +1 per 3 CL, min +1, max +3
    duration: () => minutes(1),
    effects: cl => {
      const amount = clamp(Math.floor(cl / 3), 1, 3);
      return [
        effect(amount, 'offense', 'attack', 'luck'),
        effect(amount, 'offense', 'damage', 'luck'),
      ];
    },
  },
  {
    name: 'Divine Power',
    // +1 per 3 CL, min +1, max +6; also Strength checks and Strength skills
    duration: cl => rounds(cl),
    effects: cl => {
      const amount = clamp(Math.floor(cl / 3), 1, 6);
      return [
        effect(amount, 'offense', 'attack', 'luck'),
        effect(amount, 'offense', 'damage', 'luck'),
        effect(amount, 'skills', 'Climb', 'luck'),
        effect(amount, 'skills', 'Swim', 'luck'),
      ];
    },
  },
  {
    name: 'Haste',
    // +30 ft movement (capped at double) and the extra attack are not
    // representable as numeric effects; the buff name tracks them.
    duration: cl => rounds(cl),
    effects: () => [
      effect(1, 'offense', 'attack', 'untyped'),
      effect(1, 'defense', 'ac', 'dodge'),
      effect(1, 'defense', 'reflex', 'dodge'),
    ],
  },
  {
    name: 'Fiendish Wrath',
    // Concentration + 1 round/level; tracked as 1 round/level
    duration: cl => rounds(cl),
    effects: () => [
      effect(2, 'abilityScore', 'strength', 'morale'),
      effect(2, 'abilityScore', 'constitution', 'morale'),
      effect(1, 'defense', 'will', 'morale'),
    ],
  },
  {
    name: "Bull's Strength",
    duration: cl => minutes(cl),
    effects: () => [effect(4, 'abilityScore', 'strength', 'enhancement')],
  },
  {
    name: "Bear's Endurance",
    duration: cl => minutes(cl),
    effects: () => [effect(4, 'abilityScore', 'constitution', 'enhancement')],
  },
  {
    name: "Cat's Grace",
    duration: cl => minutes(cl),
    effects: () => [effect(4, 'abilityScore', 'dexterity', 'enhancement')],
  },
  {
    name: 'Ironbeard',
    // Uses the explicit stacking natural armor type.
    duration: cl => minutes(cl),
    effects: () => [effect(1, 'defense', 'ac', 'stackingArmor')],
  },
  {
    name: 'Bone Fists',
    // Damage bonus applies to natural weapons only; it hits every damage
    // profile here, so uncheck it for non-natural attacks.
    duration: cl => minutes(cl),
    effects: () => [
      effect(1, 'defense', 'ac', 'naturalArmorStacking'),
      effect(2, 'offense', 'damage', 'untyped'),
    ],
  },
  {
    name: 'Weapon of Awe',
    duration: cl => minutes(cl),
    effects: () => [effect(2, 'offense', 'damage', 'sacred')],
  },
  {
    name: 'Stunning Barrier, Greater',
    duration: cl => rounds(cl),
    effects: () => [effect(2, 'defense', 'ac', 'deflection'), ...saves(2, 'resistance')],
  },
  {
    name: 'Ki Leech',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Wrathful Weapon',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Bit of Luck',
    duration: cl => minutes(cl * 10),
    effects: () => stealthTracker(),
  },
  {
    name: 'Long Arm',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Expeditious Retreat',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Vine Strike',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Mirror Image',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Fly',
    duration: cl => minutes(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Blessing: Destruction (Minor)',
    // Morale bonus on weapon damage = half warpriest level (min 1)
    duration: () => minutes(1),
    effects: cl => [effect(Math.max(1, Math.floor(cl / 2)), 'offense', 'damage', 'morale')],
  },
  {
    name: 'Blessing: Destruction (Major)',
    // Also grants a 50% chance to ignore crits/sneak attacks, tracked by name.
    duration: () => minutes(1),
    effects: () => [effect(4, 'offense', 'criticalConfirmation', 'insight')],
  },
  {
    name: 'Blessing: War',
    duration: () => minutes(1),
    effects: () => stealthTracker(),
  },
  {
    name: 'Channel Vigor',
    duration: cl => rounds(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Sun Metal',
    duration: cl => rounds(cl),
    effects: () => stealthTracker(),
  },
  {
    name: 'Enlarge Person',
    duration: cl => minutes(cl),
    effects: () => [
      effect(2, 'abilityScore', 'strength', 'size'),
      effect(-2, 'abilityScore', 'dexterity', 'size'),
      effect(-1, 'offense', 'attack', 'size'),
      effect(-1, 'defense', 'ac', 'size'),
      effect(+1, 'defense', 'CMB', 'size'),
      effect(+1, 'defense', 'CMD', 'size'),
    ],
  },
  {
    name: 'Heroism',
    duration: cl => minutes(cl * 10),
    effects: () => [
      effect(2, 'offense', 'attack', 'morale'),
      ...saves(2, 'morale'),
      effect(2, 'skills', 'all', 'morale'),
    ],
  },
  {
    name: 'Greater Heroism',
    // Also grants immunity to fear (tracked by name)
    duration: cl => minutes(cl),
    effects: () => [
      effect(4, 'offense', 'attack', 'morale'),
      ...saves(4, 'morale'),
      effect(4, 'skills', 'all', 'morale'),
    ],
  },
  {
    name: 'Bless',
    // The +1 on saves applies only vs fear, so it is not auto-applied
    duration: cl => minutes(cl),
    effects: () => [effect(1, 'offense', 'attack', 'morale')],
  },
  {
    name: 'Prayer',
    duration: cl => rounds(cl),
    effects: () => [
      effect(1, 'offense', 'attack', 'luck'),
      effect(1, 'offense', 'damage', 'luck'),
      ...saves(1, 'luck'),
      effect(1, 'skills', 'all', 'luck'),
    ],
  },
  {
    name: 'Protection from Evil',
    duration: cl => minutes(cl),
    effects: () => [
      effect(2, 'defense', 'ac', 'deflection'),
      ...saves(2, 'resistance'),
    ],
  },
  {
    name: 'Resist Energy',
    duration: cl => minutes(cl * 10),
    effects: () => stealthTracker(),
  },
  {
    name: 'Defending the Pride',
    duration: () => rounds(1),
    effects: () => [effect(4, 'defense', 'ac', 'morale')],
  },
  {
    name: 'Rage',
    duration: () => rounds(3),
    effects: () => [
      effect(4, 'abilityScore', 'strength', 'morale'),
      effect(4, 'abilityScore', 'constitution', 'morale'),
      effect(2, 'defense', 'will', 'untyped'),
      effect(-2, 'defense', 'ac', 'untyped'),
    ],
  },
];

function normalizeCasterLevel(casterLevel) {
  const cl = parseInt(casterLevel);
  return isNaN(cl) || cl < 1 ? 1 : cl;
}

// Build a regular personal buff from a definition at the given caster level.
function buildBuff(definition, casterLevel) {
  const cl = normalizeCasterLevel(casterLevel);
  const duration = definition.duration(cl);
  return {
    id: Math.random(),
    name: definition.name,
    active: false,
    tab: 'main',
    durationValue: String(duration.value),
    durationUnit: duration.unit,
    remainingRounds: null,
    effects: definition.effects(cl).map(e => ({ id: Math.random(), ...e })),
  };
}

export default {
  COMMON_BUFFS,
  buildBuff,
  normalizeCasterLevel,
};
